const getStoredConfigEntries = (plcConfig) => {
  if (!plcConfig || typeof plcConfig !== 'object') {
    return [];
  }

  return Object.entries(plcConfig).filter(([key]) => key !== 'schemaVersion');
};

// Takes a field map of { storedKey → currentKey } and returns a function that
// extracts compatible config values from a stored plcConfig object.
// When schema versions match the stored values are used as-is. When they differ
// (e.g. after a PLC redeployment) the map translates legacy keys and filters out
// unknown ones. The identity entries ensure same-named keys survive that path.
const createCompatibleConfigGetter = (fieldMap) => ({ plcConfig, currentSchemaVersion }) => {
  if (!plcConfig) {
    return null;
  }

  if (
    Number.isFinite(currentSchemaVersion)
    && Number.isFinite(plcConfig.schemaVersion)
    && plcConfig.schemaVersion === currentSchemaVersion
  ) {
    return Object.fromEntries(getStoredConfigEntries(plcConfig));
  }

  const normalized = getStoredConfigEntries(plcConfig).reduce((accumulator, [key, value]) => {
    const mappedKey = fieldMap[key];

    if (mappedKey === undefined || value === undefined) {
      return accumulator;
    }

    return { ...accumulator, [mappedKey]: value };
  }, {});

  return Object.keys(normalized).length > 0 ? normalized : null;
};

// ─── Bonding ─────────────────────────────────────────────────────────────────

const BONDING_FIELD_MAP = {
  // Current schema keys (identity — kept so cross-version normalization preserves them)
  autoIonizer: 'autoIonizer',
  presoakTime: 'presoakTime',
  bondTime: 'bondTime',
  superheatWaitTime: 'superheatWaitTime',
  annealingWaitTime: 'annealingWaitTime',
  finalTempWaitTime: 'finalTempWaitTime',
  upperLoadingSetpoint: 'upperLoadingSetpoint',
  lowerLoadingSetpoint: 'lowerLoadingSetpoint',
  upperBondingSetpoint: 'upperBondingSetpoint',
  upperBondingTolerance: 'upperBondingTolerance',
  lowerBondingSetpoint: 'lowerBondingSetpoint',
  lowerBondingTolerance: 'lowerBondingTolerance',
  upperSuperheatSetpoint: 'upperSuperheatSetpoint',
  upperSuperheatTolerance: 'upperSuperheatTolerance',
  lowerSuperheatSetpoint: 'lowerSuperheatSetpoint',
  lowerSuperheatTolerance: 'lowerSuperheatTolerance',
  upperAnnealingSetpoint: 'upperAnnealingSetpoint',
  upperAnnealingTolerance: 'upperAnnealingTolerance',
  lowerAnnealingSetpoint: 'lowerAnnealingSetpoint',
  lowerAnnealingTolerance: 'lowerAnnealingTolerance',
  upperFinalSetpoint: 'upperFinalSetpoint',
  lowerFinalSetpoint: 'lowerFinalSetpoint',
  // Legacy PLC config keys (transition-molder-2 and earlier)
  fChipSideLoadingTemp: 'upperLoadingSetpoint',
  fChipSideBondingTemp: 'upperBondingSetpoint',
  fChipSideSetpointTolerance: 'upperBondingTolerance',
  fChipSideSuperheatTemp: 'upperSuperheatSetpoint',
  fChipSideSuperheatTolerance: 'upperSuperheatTolerance',
  fChipSideAnnealingTemp: 'upperAnnealingSetpoint',
  fChipSideFinalTemp: 'upperFinalSetpoint',
  fCapSideLoadingTemp: 'lowerLoadingSetpoint',
  fCapSideBondingTemp: 'lowerBondingSetpoint',
  fCapSideSetpointTolerance: 'lowerBondingTolerance',
  fCapSideSuperheatTemp: 'lowerSuperheatSetpoint',
  fCapSideSuperheatTolerance: 'lowerSuperheatTolerance',
  fCapSideAnnealingTemp: 'lowerAnnealingSetpoint',
  fCapSideFinalTemp: 'lowerFinalSetpoint',
  tAnnealingWaitTime: 'annealingWaitTime',
  tBondTime: 'bondTime',
  tPresoakTime: 'presoakTime',
  tSuperheatWaitTime: 'superheatWaitTime',
};

// ─── Forming ──────────────────────────────────────────────────────────────────

const FORMING_FIELD_MAP = {
  // Current schema keys (identity — kept so cross-version normalization preserves them)
  upperLoadingSetpoint: 'upperLoadingSetpoint',
  upperFormingSetpoint: 'upperFormingSetpoint',
  upperEjectSetpoint: 'upperEjectSetpoint',
  lowerLoadingSetpoint: 'lowerLoadingSetpoint',
  lowerFormingSetpoint: 'lowerFormingSetpoint',
  lowerEjectSetpoint: 'lowerEjectSetpoint',
  formingPresoakTime: 'formingPresoakTime',
  ejectSoakTime: 'ejectSoakTime',
  autoVacuum: 'autoVacuum',
  autoIonizer: 'autoIonizer',
  // Legacy PLC config keys (transition-molder-2 and earlier)
  'fbUpperFormingHFTS.fFrontHeaterEngagedSetpoint': 'upperFormingSetpoint',
  'fbLowerFormingHFTS.fFrontHeaterEngagedSetpoint': 'lowerFormingSetpoint',
  'fbUpperFormingHFTS.fRearHeaterEngagedSetpoint': 'upperEjectSetpoint',
  'fbLowerFormingHFTS.fRearHeaterEngagedSetpoint': 'lowerEjectSetpoint',
  tPresoakDelayTime: 'formingPresoakTime',
  tCoolsoakDelayTime: 'ejectSoakTime',
};

const getCompatiblePressHMIBondingConfigValues = createCompatibleConfigGetter(BONDING_FIELD_MAP);
const getCompatiblePressHMIFormingConfigValues = createCompatibleConfigGetter(FORMING_FIELD_MAP);

export {
  getCompatiblePressHMIBondingConfigValues,
  getCompatiblePressHMIFormingConfigValues,
};
